import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

function samplePoints(draw, count) {
  const size = 220;
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff";
  draw(ctx, size);
  const data = ctx.getImageData(0, 0, size, size).data;
  const pts = new Float32Array(count * 3);
  let filled = 0, guard = 0;
  while (filled < count && guard < count * 400) {
    guard++;
    const x = Math.random() * size, y = Math.random() * size;
    const idx = ((y | 0) * size + (x | 0)) * 4 + 3;
    if (data[idx] > 128) {
      pts[filled * 3] = (x / size - 0.5) * 7.2;
      pts[filled * 3 + 1] = (0.5 - y / size) * 7.2;
      pts[filled * 3 + 2] = (Math.random() - 0.5) * 1.4;
      filled++;
    }
  }
  for (; filled < count; filled++) {
    const src = ((Math.random() * guard) | 0) % Math.max(1, filled);
    pts[filled * 3] = pts[src * 3];
    pts[filled * 3 + 1] = pts[src * 3 + 1];
    pts[filled * 3 + 2] = (Math.random() - 0.5) * 1.4;
  }
  return pts;
}

function drawKey(ctx, s) {
  const u = s / 220;
  ctx.save();
  ctx.translate(s / 2, 0);
  ctx.beginPath();
  ctx.arc(0, 58 * u, 38 * u, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(0, 58 * u, 17 * u, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";
  ctx.fillRect(-8 * u, 92 * u, 16 * u, 98 * u);
  ctx.fillRect(8 * u, 152 * u, 26 * u, 11 * u);
  ctx.fillRect(8 * u, 172 * u, 36 * u, 11 * u);
  ctx.restore();
}

function drawHouse(ctx, s) {
  const u = s / 220;
  ctx.save();
  ctx.translate(s / 2, s / 2);
  ctx.beginPath();
  ctx.moveTo(0, -88 * u);
  ctx.lineTo(86 * u, -12 * u);
  ctx.lineTo(-86 * u, -12 * u);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(-68 * u, -12 * u, 136 * u, 98 * u);
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillRect(-16 * u, 24 * u, 32 * u, 62 * u);
  ctx.restore();
}


function drawArch(ctx, s) {
  const u = s / 220;
  ctx.save();
  ctx.translate(s / 2, s / 2);
  ctx.beginPath();
  ctx.moveTo(-72 * u, 92 * u);
  ctx.lineTo(-72 * u, -18 * u);
  ctx.arc(0, -18 * u, 72 * u, Math.PI, 0);
  ctx.lineTo(72 * u, 92 * u);
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.moveTo(-42 * u, 92 * u);
  ctx.lineTo(-42 * u, -18 * u);
  ctx.arc(0, -18 * u, 42 * u, Math.PI, 0);
  ctx.lineTo(42 * u, 92 * u);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

export const ParticleCanvas = ({ scrollTriggerRef, finalShape = "house" }) => {
  const mountRef = useRef(null);
  const [assembled, setAssembled] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 9;

    const keyPts = samplePoints(drawKey, COUNT);
    const housePts = samplePoints(finalShape === "arch" ? drawArch : drawHouse, COUNT);
    const scatter = new Float32Array(COUNT * 3);
    const burst = new Float32Array(COUNT * 3);
    const seeds = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      scatter[i * 3] = (Math.random() - 0.5) * 16;
      scatter[i * 3 + 1] = (Math.random() - 0.5) * 16;
      scatter[i * 3 + 2] = (Math.random() - 0.5) * 8;
      const r = 6 + Math.random() * 8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      burst[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      burst[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) - 2;
      burst[i * 3 + 2] = r * Math.cos(phi) * 0.5;
      seeds[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.6 + Math.random() * 0.8;
    }

    const positions = new Float32Array(scatter);
    const colors = new Float32Array(COUNT * 3);
    const base = new THREE.Color(0xffffff);
    for (let i = 0; i < COUNT; i++) {
      const b = 0.8 + Math.random() * 0.4;
      colors[i * 3] = base.r * b;
      colors[i * 3 + 1] = base.g * b;
      colors[i * 3 + 2] = base.b * b;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(geo, mat));

    const state = { assemble: 0, scroll: 0 };
    gsap.to(state, { assemble: 1, duration: 1.4, ease: "power2.inOut", delay: 0.2, onComplete: () => setAssembled(true) });

    let st;
    if (scrollTriggerRef?.current) {
      st = ScrollTrigger.create({
        trigger: scrollTriggerRef.current,
        start: "top top",
        end: "+=250%",
        scrub: true,
        onUpdate: (self) => { state.scroll = self.progress; },
      });
    }

    let raf;
    const clock = new THREE.Clock();
    const tick = () => {
      const t = clock.getElapsedTime();
      const pos = geo.attributes.position.array;
      const a = easeInOut(state.assemble);
      const p = state.scroll;
      for (let i = 0; i < COUNT; i++) {
        const i3 = i * 3;
        let x, y, z;
        if (p <= 0.001) {
          x = scatter[i3] + (keyPts[i3] - scatter[i3]) * a;
          y = scatter[i3 + 1] + (keyPts[i3 + 1] - scatter[i3 + 1]) * a;
          z = scatter[i3 + 2] + (keyPts[i3 + 2] - scatter[i3 + 2]) * a;
        } else if (p < 0.35) {
          const tt = easeOutCubic(Math.min(1, (p / 0.35) * speeds[i] * 1.4));
          x = keyPts[i3] + (burst[i3] - keyPts[i3]) * tt;
          y = keyPts[i3 + 1] + (burst[i3 + 1] - keyPts[i3 + 1]) * tt;
          z = keyPts[i3 + 2] + (burst[i3 + 2] - keyPts[i3 + 2]) * tt;
        } else if (p < 0.7) {
          const tt = easeInOut((p - 0.35) / 0.35);
          x = burst[i3] + (housePts[i3] - burst[i3]) * tt;
          y = burst[i3 + 1] + (housePts[i3 + 1] - burst[i3 + 1]) * tt;
          z = burst[i3 + 2] + (housePts[i3 + 2] - burst[i3 + 2]) * tt;
        } else {
          x = housePts[i3];
          y = housePts[i3 + 1];
          z = housePts[i3 + 2];
        }
        const n = 0.045;
        pos[i3] = x + Math.sin(t * speeds[i] * 1.6 + seeds[i]) * n;
        pos[i3 + 1] = y + Math.cos(t * speeds[i] * 1.3 + seeds[i] * 1.7) * n;
        pos[i3 + 2] = z + Math.sin(t * speeds[i] + seeds[i] * 0.6) * n;
      }
      geo.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      st?.kill();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, [scrollTriggerRef, finalShape]);

  return (
    <div ref={mountRef} className="absolute inset-0" data-testid="particle-canvas">
      <div className="particle-shimmer" style={{ opacity: assembled ? 0 : 1 }}>
        <span />
      </div>
    </div>
  );
};
